import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import {
  useCreateVehicleTypeOverrideMutation,
  useUpdateVehicleTypeOverrideMutation,
} from '@/api/adminServiceApi'

// Кириллические двойники → латиница (как в billing), без пробелов и дефисов.
const CYRILLIC_TO_LATIN = {
  А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H',
  О: 'O', Р: 'P', С: 'C', Т: 'T', У: 'Y', Х: 'X',
}
const normalizePlate = (value) => String(value || '')
  .toUpperCase()
  .replace(/[\s-]/g, '')
  .replace(/[АВЕКМНОРСТУХ]/g, (ch) => CYRILLIC_TO_LATIN[ch] || ch)

const getErrorMessage = (error: any, t) => {
  if (!error) return ''
  return error?.data?.message || error?.error || t('error_occurred')
}

const OverrideDialog = ({
  open,
  mode,
  item,
  buildingId,
  vehicleTypeOptions,
  onClose,
}) => {
  const { t } = useTranslation()
  const isEdit = mode === 'edit'
  const [plateNumber, setPlateNumber] = useState(item?.plate_number || '')
  const [vehicleType, setVehicleType] = useState(() => {
    if (!item?.vehicle_type) return ''
    const match = vehicleTypeOptions.find(
      (o) => o.value.toLowerCase() === item.vehicle_type.toLowerCase(),
    )
    return match?.value || item.vehicle_type
  })
  const [comment, setComment] = useState(item?.comment || '')

  const [createOverride, createState] = useCreateVehicleTypeOverrideMutation()
  const [updateOverride, updateState] = useUpdateVehicleTypeOverrideMutation()
  const isSaving = createState.isLoading || updateState.isLoading
  const error = isEdit ? updateState.error : createState.error

  const options = vehicleTypeOptions.some((o) => o.value === vehicleType) || !vehicleType
    ? vehicleTypeOptions
    : [{ value: vehicleType, label: vehicleType }, ...vehicleTypeOptions]

  const isValid = (isEdit || normalizePlate(plateNumber).length >= 3) && !!vehicleType

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!isValid || isSaving) return
    try {
      if (isEdit) {
        await updateOverride({
          buildingId,
          id: item.id,
          vehicleType,
          comment: comment.trim(),
        }).unwrap()
      } else {
        await createOverride({
          buildingId,
          plateNumber: normalizePlate(plateNumber),
          vehicleType,
          comment: comment.trim(),
        }).unwrap()
      }
      onClose()
    } catch {
      // ошибка показывается в Alert
    }
  }

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEdit ? t('vehicle_type_overrides_edit') : t('vehicle_type_overrides_add')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('plate_number')}
              value={plateNumber}
              onChange={(e) => setPlateNumber(normalizePlate(e.target.value))}
              disabled={isEdit || isSaving}
              required
              size="small"
              slotProps={{ htmlInput: { maxLength: 15 } }}
            />
            <TextField
              select
              label={t('vehicle_type')}
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              disabled={isSaving}
              required
              size="small"
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('comment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSaving}
              multiline
              minRows={2}
              size="small"
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <Alert severity="info">
              {t('vehicle_type_overrides_hint')}
            </Alert>
            {error && <Alert severity="error">{getErrorMessage(error, t)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>{t('cancel')}</Button>
          <Button type="submit" variant="contained" disabled={!isValid || isSaving}>
            {t('save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default OverrideDialog
